import {
    ApiBulkUpdateStoreItem,
    BulkUpdateItemsApiResponse,
    BulkUpdateStoreRow,
  } from '../../features/pricebook/types/items';
  
  class BulkUpdateStoreItemModel {
    private _serverData: ApiBulkUpdateStoreItem;
  
    constructor(serverData: ApiBulkUpdateStoreItem) {
      this._serverData = serverData;
    }
  
    get found() { return this._serverData?.found ?? false; }
    get scan_code() { return this._serverData?.scan_code ?? ''; }
    get store_id() { return this._serverData?.store_id ?? ''; }
    get store_name() { return this._serverData?.store_name ?? ''; }
    get item_name() { return this._serverData?.item_name ?? ''; }
    get dept_code() { return this._serverData?.dept_code ?? ''; }
    get tax_code() { return this._serverData?.tax_code ?? ''; }
    get vendor_id() { return this._serverData?.vendor_id ?? ''; }
    get pricegroup() { return this._serverData?.pricegroup ?? ''; }
    get food_stampable() { return this._serverData?.food_stampable ?? null; }
    get age_restriction() { return this._serverData?.age_restriction ?? null; }
    get linked_code() { return this._serverData?.linked_code ?? ''; }
    get net_cost() { return this._serverData?.net_cost ?? null; }
    get retail_price() { return this._serverData?.retail_price ?? null; }
    get margin() { return this._serverData?.margin ?? null; }
  
    getRawData() {
      return this._serverData;
    }
  
    toRowFormat(showScanCode: boolean, groupClassName: string): BulkUpdateStoreRow {
      return {
        scanCode: this.scan_code,
        storeId: this.store_id,
        storeName: this.store_name,
        itemName: this.item_name,
        department: this.dept_code,
        taxType: this.tax_code,
        vendor: this.vendor_id,
        priceGroup: this.pricegroup || '',
        foodstamp: this.food_stampable === true ? 'Yes' : this.food_stampable === false ? 'No' : '',
        ageRestriction: BulkUpdateStoreItemModel.normalizeAgeRestrictionValue(this.age_restriction),
        linkedItem: this.linked_code || '',
        netCost: BulkUpdateStoreItemModel.formatCurrency(this.net_cost),
        retailPrice: BulkUpdateStoreItemModel.formatCurrency(this.retail_price),
        marginAfterRebate: BulkUpdateStoreItemModel.formatNumber(this.margin),
        newPrice: '',
        rowId: `${this.scan_code}-${this.store_id}`,
        showScanCode,
        groupClassName,
        rawItem: this.getRawData(),
      };
    }
  
    static fromServerData(serverData: ApiBulkUpdateStoreItem) {
      return new BulkUpdateStoreItemModel(serverData);
    }
  
    private static formatCurrency(value: number | null) {
      if (value === null || value === undefined) return '-';
      return `$${Number(value).toFixed(2)}`;
    }
  
    private static formatNumber(value: number | null) {
      if (value === null || value === undefined) return '-';
      return Number(value).toFixed(2);
    }
  
    private static normalizeAgeRestrictionValue(value?: string | number | null): string {
      if (value === null || value === undefined) return '';
      const raw = String(value).trim();
      if (!raw) return '';
      if (/^\d+$/.test(raw)) return raw;
  
      const digitsMatch = raw.match(/(\d+)/);
      return digitsMatch ? digitsMatch[1] : '';
    }
  }
  
  class BulkUpdateItemsListModel {
    private _groups: Array<{ scanCode: string; stores: BulkUpdateStoreItemModel[] }>;
    private _pagination: {
      page: number;
      page_size: number;
      total_pages: number;
      has_next: boolean;
      has_previous: boolean;
      total_count: number;
    } | null;
  
    constructor(serverData: BulkUpdateItemsApiResponse | null = null) {
      if (serverData) {
        this._groups = Object.entries(serverData.items ?? {}).reduce<Array<{ scanCode: string; stores: BulkUpdateStoreItemModel[] }>>(
          (acc, [scanCode, group]) => {
            const stores = Object.values(group?.stores ?? {})
              .map((store) => BulkUpdateStoreItemModel.fromServerData(store))
              .filter((store) => store.found);
  
            if (stores.length > 0) {
              acc.push({ scanCode, stores });
            }
  
            return acc;
          },
          []
        );
        this._pagination = {
          page: serverData.page ?? 1,
          page_size: serverData.page_size ?? 20,
          total_pages: serverData.total_pages ?? 0,
          has_next: serverData.has_next ?? false,
          has_previous: serverData.has_previous ?? false,
          total_count: serverData.total_count ?? 0,
        };
      } else {
        this._groups = [];
        this._pagination = null;
      }
    }
  
    get groups() { return this._groups; }
    get items() { return this._groups.flatMap((group) => group.stores); }
    get pagination() { return this._pagination; }
  
    toRowFormat(): BulkUpdateStoreRow[] {
      return this._groups.flatMap((group, groupIndex) =>
        group.stores.map((store, storeIndex) =>
          store.toRowFormat(
            storeIndex === 0,
            groupIndex % 2 === 0 ? 'bulk-update-row-group--odd' : 'bulk-update-row-group--even'
          )
        )
      );
    }
  
    static fromServerData(serverData: BulkUpdateItemsApiResponse) {
      return new BulkUpdateItemsListModel(serverData);
    }
  
    static createEmpty() {
      return new BulkUpdateItemsListModel();
    }
  }
  
  export default BulkUpdateStoreItemModel;
  export { BulkUpdateItemsListModel };
  